import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, ClipboardList, Package, Factory, ShieldCheck,
  Boxes, Users, LogOut, Scale, ListChecks, Trash2, UserCog, CalendarClock,
  Repeat, Bell, ChevronDown, ChevronRight, CalendarCheck, AlertTriangle, Cog, Lightbulb, Gauge,
  PanelLeftClose, PanelLeftOpen, GripVertical, RotateCcw,
} from "lucide-react";

const nav = [
  { to: "/", label: "Gösterge Paneli", icon: LayoutDashboard, end: true },
  { to: "/siparisler", label: "Gelen Siparişler", icon: ClipboardList },
  { to: "/termin-durumu", label: "Termin Durumu", icon: CalendarClock },
  { to: "/uretim", label: "Üretim Girişi", icon: Factory },
  { to: "/uretim-planlama", label: "Üretim Planlama", icon: CalendarCheck, children: [
    { to: "/planlama-oneri", label: "Planlama Öneri", icon: Lightbulb },
    { to: "/onerilen-biskuvi", label: "Önerilen Bisküvi", icon: CalendarClock },
  ] },
  { to: "/makine-performans", label: "Makine Performansı", icon: Gauge },
  { to: "/kalite", label: "Kalite Takibi", icon: ShieldCheck },
  { to: "/yapilan-urun-musterisi", label: "Yapılan Ürün Müşterisi", icon: ListChecks },
  { to: "/stok", label: "Bisküvi Stok", icon: Boxes, children: [
    { to: "/devir-biskuvi", label: "Devir Bisküvi", icon: Repeat },
    { to: "/devir-kaliteden-gecmeyen", label: "Kaliteden Geçmeyen Devir", icon: Repeat },
    { to: "/sekillenecek-makina", label: "Şekillenecek Ürün Makina", icon: Cog },
    { to: "/sekillendirme-plani", label: "Şekillendirme Planı", icon: CalendarCheck },
    { to: "/makina-kapasitesi", label: "Makina Kapasitesi", icon: Gauge },
  ] },
  { to: "/kg-rapor", label: "KG Detay Raporu", icon: Scale, children: [
    { to: "/aylik-rapor", label: "Aylık Rapor", icon: CalendarClock },
  ] },
  { to: "/fazla-uretim", label: "Siparişten Fazla Yapılan Ürünler", icon: AlertTriangle },
  { to: "/malzemeler", label: "Malzemeler", icon: Package },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: UserCog, adminOnly: true },
  { to: "/bildirimler", label: "Bildirimler", icon: Bell, adminOnly: true, notif: true },
  { to: "/geri-donusum", label: "Geri Dönüşüm", icon: Trash2 },
];

const linkClass = (child, collapsed) => ({ isActive }) =>
  `group relative flex items-center gap-3 rounded-lg mx-2 my-0.5 py-2.5 text-sm transition-all duration-200 ${
    collapsed ? "justify-center px-2" : (child ? "pl-3 pr-3 text-[13px]" : "pl-3 pr-3")
  } ${
    isActive
      ? "bg-gradient-to-r from-amber-500/20 to-amber-500/5 text-amber-300 font-semibold ring-1 ring-inset ring-amber-500/30"
      : "text-zinc-400 hover:bg-white/5 hover:text-white"
  }`;

const IconChip = ({ children }) => (
  <span className="grid place-items-center h-7 w-7 shrink-0 rounded-md bg-white/5 ring-1 ring-white/5 group-hover:bg-white/10 transition-colors">
    {children}
  </span>
);

const roleLabel = (r) => (r === "admin" ? "Yönetici" : r === "viewer" ? "İzleyici" : "Kullanıcı");

const testId = (to) => `nav-${to === "/" ? "dashboard" : to.replace("/", "")}`;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("navOpenGroups") || "{}"); } catch { return {}; }
  });
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("navCollapsed") === "1");
  const [notifCount, setNotifCount] = useState(0);
  const [navOrder, setNavOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("navOrder") || "[]"); } catch { return []; }
  });
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/notifications").then((r) => setNotifCount(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, [user]);

  const toggleGroup = (key) => setOpenGroups((g) => {
    const n = { ...g, [key]: !g[key] };
    localStorage.setItem("navOpenGroups", JSON.stringify(n));
    return n;
  });

  const toggleCollapsed = () => setCollapsed((c) => {
    localStorage.setItem("navCollapsed", c ? "0" : "1");
    return !c;
  });

  const items = nav.filter((n) => !n.adminOnly || user?.role === "admin");
  const pos = {}; navOrder.forEach((to, i) => { pos[to] = i; });
  const ordered = [...items].sort((a, b) => (pos[a.to] ?? 999) - (pos[b.to] ?? 999));

  const saveOrder = (arr) => { setNavOrder(arr); localStorage.setItem("navOrder", JSON.stringify(arr)); };
  const onNavDrop = (targetTo) => {
    if (!dragKey || dragKey === targetTo) { setDragKey(null); setDragOverKey(null); return; }
    const curr = ordered.map((n) => n.to);
    const from = curr.indexOf(dragKey);
    const to = curr.indexOf(targetTo);
    if (from < 0 || to < 0) { setDragKey(null); setDragOverKey(null); return; }
    curr.splice(to, 0, curr.splice(from, 1)[0]);
    saveOrder(curr);
    setDragKey(null); setDragOverKey(null);
  };
  const resetOrder = () => { localStorage.removeItem("navOrder"); setNavOrder([]); };

  return (
    <div className="flex min-h-screen">
      <aside className={`${collapsed ? "w-16" : "w-64"} shrink-0 bg-gradient-to-b from-[#1c1c20] to-[#141416] text-zinc-300 flex flex-col fixed h-screen border-r border-white/5 shadow-2xl transition-[width] duration-200`} data-testid="sidebar">
        <div className={`border-b border-white/5 ${collapsed ? "px-2 py-4" : "px-5 py-5"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            <span className="grid place-items-center h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-white ring-1 ring-amber-300/40 shadow-lg shadow-amber-900/30">
              <img src="/by-bone-logo.png" alt="BY BONE" className="h-full w-full object-contain p-1.5" />
            </span>
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-head font-black text-white text-[15px] leading-tight tracking-tight">BY BONE ÜRETİM TAKİP</div>
                <div className="mt-0.5 text-[13px] font-head font-black italic tracking-widest bg-gradient-to-r from-amber-400 via-yellow-300 to-orange-500 bg-clip-text text-transparent">MUSTAFA TASARIM</div>
              </div>
            )}
          </div>
        </div>

        <button onClick={toggleCollapsed} data-testid="sidebar-toggle"
          title={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
          className="absolute right-2 top-6 z-10 grid place-items-center h-6 w-6 rounded-full bg-amber-500 text-zinc-900 shadow-md ring-2 ring-[#141416] hover:bg-amber-400 transition-colors">
          {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
        </button>

        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
          {ordered.map((n) => {
            const content = n.children ? (
              <div className="mb-0.5">
                <div className="relative flex items-center">
                  <NavLink to={n.to} end={n.end} data-testid={testId(n.to)} title={collapsed ? n.label : undefined}
                    className={(s) => `flex-1 ${linkClass(false, collapsed)(s)}`}>
                    <IconChip><n.icon size={16} /></IconChip>
                    {!collapsed && <span className="flex-1">{n.label}</span>}
                  </NavLink>
                  {!collapsed && (
                    <button onClick={() => toggleGroup(n.to)} data-testid={`nav-group-${n.to.replace("/", "")}`}
                      className="absolute right-3 p-1 rounded-md text-zinc-500 hover:text-amber-300 hover:bg-white/5 transition-colors">
                      {openGroups[n.to] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </button>
                  )}
                </div>
                {!collapsed && openGroups[n.to] && (
                  <div className="ml-6 border-l border-white/10 pl-1 my-0.5">
                    {n.children.map((c) => (
                      <NavLink key={c.to} to={c.to} data-testid={testId(c.to)} className={linkClass(true, false)}>
                        <IconChip><c.icon size={14} /></IconChip> {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink to={n.to} end={n.end} data-testid={testId(n.to)} title={collapsed ? n.label : undefined}
                className={linkClass(false, collapsed)}>
                <IconChip><n.icon size={16} /></IconChip>
                {!collapsed && <span className="flex-1">{n.label}</span>}
                {n.notif && notifCount > 0 && (
                  <span className={`bg-red-500 text-white text-[10px] font-bold rounded-full shadow shadow-red-900/40 ${collapsed ? "absolute top-1 right-1 h-4 min-w-[16px] px-1 grid place-items-center" : "ml-auto px-1.5 py-0.5"}`} data-testid="notif-badge">{notifCount}</span>
                )}
              </NavLink>
            );
            return (
              <div key={n.to}
                draggable={!collapsed}
                onDragStart={(e) => { setDragKey(n.to); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); if (dragKey && dragOverKey !== n.to) setDragOverKey(n.to); }}
                onDrop={() => onNavDrop(n.to)}
                onDragEnd={() => { setDragKey(null); setDragOverKey(null); }}
                data-testid={`nav-drag-${n.to === "/" ? "dashboard" : n.to.replace("/", "")}`}
                className={`relative group/drag ${!collapsed ? "cursor-grab active:cursor-grabbing" : ""} ${dragKey === n.to ? "opacity-40" : ""} ${dragOverKey === n.to && dragKey && dragKey !== n.to ? "before:absolute before:-top-px before:left-2 before:right-2 before:h-0.5 before:bg-amber-400 before:rounded-full" : ""}`}>
                {!collapsed && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 z-10 text-zinc-600 opacity-0 group-hover/drag:opacity-100 transition-opacity pointer-events-none">
                    <GripVertical size={12} />
                  </span>
                )}
                {content}
              </div>
            );
          })}
        </nav>

        {!collapsed && navOrder.length > 0 && (
          <button onClick={resetOrder} data-testid="nav-reset-order"
            className="mx-3 mb-1 flex items-center justify-center gap-1.5 rounded-md py-1.5 text-[11px] font-semibold text-zinc-500 hover:text-amber-300 hover:bg-white/5 transition-colors">
            <RotateCcw size={12} /> Menü sırasını sıfırla
          </button>
        )}

        <div className="border-t border-white/5 p-3">
          <div className={`flex items-center rounded-lg bg-zinc-900 ring-1 ring-zinc-800 ${collapsed ? "flex-col gap-2 px-2 py-2.5" : "gap-3 px-3 py-2.5"}`}>
            <span className="grid place-items-center h-9 w-9 shrink-0 overflow-hidden rounded-full bg-white ring-1 ring-zinc-200">
              <img src="/by-bone-logo.png" alt="BY BONE" className="h-full w-full object-contain p-1" />
            </span>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-white truncate" data-testid="current-user">{user?.name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-white">{roleLabel(user?.role)}</div>
              </div>
            )}
            <button onClick={logout} data-testid="logout-btn" title="Çıkış Yap"
              className="grid place-items-center h-8 w-8 rounded-md text-zinc-300 hover:text-red-400 hover:bg-red-500/10 transition-colors">
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>
      <main className={`flex-1 ${collapsed ? "ml-16" : "ml-64"} min-w-0 transition-[margin] duration-200`}>{children}</main>
    </div>
  );
}
